import {
  ClerkProvider,
  SignIn,
  SignUp,
  UserButton,
  useAuth as useClerkAuth,
  useUser as useClerkUser,
} from '@clerk/react'
import { useQueryClient } from '@tanstack/react-query'
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'

import { runtimeConfigValue } from './runtime-config'

export interface AppAuthUser {
  id: string
  firstName?: string | null
  fullName?: string | null
}

interface AppAuthValue {
  isLoaded: boolean
  isSignedIn: boolean
  getToken: () => Promise<string | null>
  apiAuthMode: 'required' | 'optional'
  user: AppAuthUser | null
  isPreviewMode: boolean
  isTokenUnavailable: boolean
  isTokenRetrying: boolean
  authRecoveryGeneration: number
  retryToken: () => Promise<void>
}

const AppAuthContext = createContext<AppAuthValue | null>(null)

const clerkPubKey = runtimeConfigValue(
  'VITE_CLERK_PUBLISHABLE_KEY',
  import.meta.env.VITE_CLERK_PUBLISHABLE_KEY,
)
const previewEnabled = import.meta.env.VITE_ENABLE_PREVIEW_AUTH === 'true'
const previewUserName = import.meta.env.VITE_PREVIEW_USER_NAME || 'Preview User'
const previewBearerToken = import.meta.env.VITE_PREVIEW_BEARER_TOKEN || null

function PreviewAuthProvider({ children }: { children: React.ReactNode }) {
  const value = useMemo<AppAuthValue>(
    () => ({
      isLoaded: true,
      isSignedIn: true,
      getToken: async () => previewBearerToken,
      apiAuthMode: previewBearerToken ? 'required' : 'optional',
      user: {
        id: 'preview-user',
        firstName: previewUserName,
        fullName: previewUserName,
      },
      isPreviewMode: true,
      isTokenUnavailable: false,
      isTokenRetrying: false,
      authRecoveryGeneration: 0,
      retryToken: async () => {},
    }),
    [],
  )

  return (
    <AppAuthContext.Provider value={value}>
      {children}
    </AppAuthContext.Provider>
  )
}

export function ClerkAuthBridge({ children }: { children: React.ReactNode }) {
  const { getToken, isLoaded, isSignedIn } = useClerkAuth()
  const { user } = useClerkUser()
  const queryClient = useQueryClient()
  const [isTokenUnavailable, setIsTokenUnavailable] = useState(false)
  const [isTokenRetrying, setIsTokenRetrying] = useState(false)
  const [authRecoveryGeneration, setAuthRecoveryGeneration] = useState(0)

  const getAppToken = useCallback(async () => {
    try {
      const token = await getToken()
      setIsTokenUnavailable(!token)
      return token
    } catch {
      setIsTokenUnavailable(true)
      console.error('Failed to get Clerk authentication token')
      throw new Error('Clerk authentication token is unavailable')
    }
  }, [getToken])

  const retryToken = useCallback(async () => {
    setIsTokenRetrying(true)
    try {
      const token = await getAppToken()
      if (token) {
        setAuthRecoveryGeneration((generation) => generation + 1)
        await queryClient.resetQueries()
      }
    } catch {
      // getAppToken already records the recoverable unavailable state.
    } finally {
      setIsTokenRetrying(false)
    }
  }, [getAppToken, queryClient])

  const value = useMemo<AppAuthValue>(
    () => ({
      isLoaded,
      isSignedIn: !!isSignedIn,
      getToken: getAppToken,
      apiAuthMode: 'required',
      user: user
        ? {
            id: user.id,
            firstName: user.firstName,
            fullName: user.fullName,
          }
        : null,
      isPreviewMode: false,
      isTokenUnavailable,
      isTokenRetrying,
      authRecoveryGeneration,
      retryToken,
    }),
    [
      getAppToken,
      authRecoveryGeneration,
      isLoaded,
      isSignedIn,
      isTokenRetrying,
      isTokenUnavailable,
      retryToken,
      user,
    ],
  )

  return (
    <AppAuthContext.Provider value={value}>
      {children}
    </AppAuthContext.Provider>
  )
}

function UnconfiguredAuthProvider({ children }: { children: React.ReactNode }) {
  const value = useMemo<AppAuthValue>(
    () => ({
      isLoaded: true,
      isSignedIn: false,
      getToken: async () => null,
      apiAuthMode: 'optional',
      user: null,
      isPreviewMode: false,
      isTokenUnavailable: false,
      isTokenRetrying: false,
      authRecoveryGeneration: 0,
      retryToken: async () => {},
    }),
    [],
  )

  return (
    <AppAuthContext.Provider value={value}>
      {children}
    </AppAuthContext.Provider>
  )
}

export function AppAuthProvider({ children }: { children: React.ReactNode }) {
  if (previewEnabled) {
    return <PreviewAuthProvider>{children}</PreviewAuthProvider>
  }

  if (!clerkPubKey) {
    console.warn('Missing VITE_CLERK_PUBLISHABLE_KEY - UI auth is disabled')
    return <UnconfiguredAuthProvider>{children}</UnconfiguredAuthProvider>
  }

  return (
    <ClerkProvider publishableKey={clerkPubKey} afterSignOutUrl="/login">
      <ClerkAuthBridge>{children}</ClerkAuthBridge>
    </ClerkProvider>
  )
}

export function useAppAuth() {
  const context = useContext(AppAuthContext)
  if (!context) {
    throw new Error('useAppAuth must be used inside AppAuthProvider')
  }
  return context
}

export function AuthUserButton() {
  const {
    isPreviewMode,
    isTokenRetrying,
    isTokenUnavailable,
    retryToken,
    user,
  } = useAppAuth()

  if (isPreviewMode) {
    return (
      <div className="rounded-full border px-3 py-1 text-sm text-muted-foreground">
        Preview: {user?.fullName ?? user?.firstName ?? 'User'}
      </div>
    )
  }

  if (!clerkPubKey) {
    return (
      <div className="rounded-full border px-3 py-1 text-sm text-muted-foreground">
        Auth 未設定
      </div>
    )
  }

  return (
    <div className="flex items-center gap-3">
      {isTokenUnavailable && (
        <AuthTokenUnavailableNotice
          isRetrying={isTokenRetrying}
          onRetry={retryToken}
        />
      )}
      <UserButton />
    </div>
  )
}

export function AuthTokenUnavailableNotice({
  isRetrying,
  onRetry,
}: {
  isRetrying: boolean
  onRetry: () => Promise<void>
}) {
  return (
    <div
      role="alert"
      className="flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-950"
    >
      <span>認証情報を取得できません。</span>
      <button
        type="button"
        className="font-medium underline underline-offset-2 disabled:cursor-wait disabled:opacity-60"
        disabled={isRetrying}
        onClick={() => void onRetry()}
      >
        {isRetrying ? '再試行中…' : '再試行'}
      </button>
      <a className="font-medium underline underline-offset-2" href="/login">
        再ログイン
      </a>
    </div>
  )
}

export function LoginContent() {
  const { isPreviewMode } = useAppAuth()

  if (isPreviewMode) {
    return (
      <div className="space-y-4 text-center">
        <p className="text-sm text-muted-foreground">
          Preview mode が有効です。ログインなしで UI を確認できます。
        </p>
        <a
          href="/"
          className="inline-flex rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          ダッシュボードへ
        </a>
      </div>
    )
  }

  if (!clerkPubKey) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-left text-sm text-amber-900">
        Clerk の公開鍵が未設定です。`VITE_CLERK_PUBLISHABLE_KEY` を設定するか、
        preview 用に `VITE_ENABLE_PREVIEW_AUTH=true` を利用してください。
      </div>
    )
  }

  return (
    <SignIn
      routing="path"
      path="/login"
      signUpUrl="/signup"
      fallbackRedirectUrl="/"
    />
  )
}

export function SignUpContent() {
  const { isPreviewMode } = useAppAuth()

  if (isPreviewMode) {
    return (
      <div className="space-y-4 text-center">
        <p className="text-sm text-muted-foreground">
          Preview mode が有効です。アカウント登録は不要です。
        </p>
        <a
          href="/"
          className="inline-flex rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          ダッシュボードへ
        </a>
      </div>
    )
  }

  if (!clerkPubKey) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-left text-sm text-amber-900">
        Clerk の公開鍵が未設定です。`VITE_CLERK_PUBLISHABLE_KEY` を設定するか、
        preview 用に `VITE_ENABLE_PREVIEW_AUTH=true` を利用してください。
      </div>
    )
  }

  return (
    <SignUp
      routing="path"
      path="/signup"
      signInUrl="/login"
      fallbackRedirectUrl="/"
    />
  )
}
